import React, { useContext, useEffect, useRef, useMemo } from 'react'
import { DAGTask, FileReference, TaskReference, TaskPathReturn } from '@pollination-solutions/pollination-sdk'
import { RootContext as context } from '~/state'
import { graphics } from '~/utils'
import { DAGOutput } from '~/types/queenbee/io'

interface FlowOutputNodeProps {
  output: DAGOutput
  tasks: DAGTask[]
}

export const FlowOutputNode = ({ output, tasks }: FlowOutputNodeProps): JSX.Element => {
  const { graph, register, highlight } = useContext(context)

  const nodeRef = useRef<HTMLDivElement>(null)

  const output_type = output.type?.toLowerCase() as string

  const dependencies = useMemo(() => {
    if (output_type.includes('path') || output_type.includes('folder') || output_type.includes('file')) {
      const artifactSourceType = output.from.type?.toLowerCase() as string

      if (artifactSourceType.includes('task')) {
        const { name } = output.from as TaskReference

        return [name]
      } else {
        const output_source = output.from as FileReference
        const { path } = output_source

        const matches: string[] = []

        tasks.forEach((task) => {
          const { returns } = task

          const outputMatch = returns?.find((out) => {
            const key = (out as TaskPathReturn).path
              ?.split('/')
              .slice(0, path.length - 1)
              .join('')

            return key === path
          })

          const subFolderMatch = task.sub_folder?.includes(path)

          if (outputMatch || subFolderMatch) {
            matches.push(task.name)
          }
        })
        return matches
      }
    } else {
      const { name } = output.from as TaskReference
      return [name]
    }
  }, [output, output_type, tasks])

  const icon = graphics.getInputIcon(output_type)

  const key = `output-${output.name}`

  useEffect(() => {
    const isRegistered = graph?.nodes?.find((node) => node.key === key)

    if (!isRegistered) {
      register(key, nodeRef, dependencies)
    }
  }, [key, dependencies, graph, register])

  const isFaded = (graph?.highlight?.length ?? 0 > 0) && !graph?.highlight?.includes(key)

  return (
    <div
      className="output-container"
      style={{ opacity: isFaded ? 0.2 : 1, transition: 'opacity', transitionDuration: '300ms' }}
      onPointerEnter={() => highlight(key)}
      onPointerLeave={() => highlight(undefined)}
    >
      <div className="output-node" ref={nodeRef}>
        {icon}
      </div>
      <div className="output-label">
        <h2>{output.name}</h2>
      </div>
      <style global jsx>{`
        .output-node > svg {
          transform: rotate(135deg) scale(1.5);
        }
      `}</style>
      <style jsx>{`
        .output-container {
          display: flex;
          align-items: center;
          box-sizing: content-box;
        }

        .output-node {
          min-width: 34px;
          min-height: 34px;
          box-sizing: content-box;
          margin-top: 12px;
          margin-bottom: 12px;
          border-radius: 34px;
          border-top-right-radius: 0;
          border: 2px solid #333;
          background: #fcfcfc;
          display: flex;
          justify-content: center;
          align-items: center;
          transform: rotate(-135deg) translate(2px, -2px);
        }

        .output-label {
          min-width: 50px;
          width: 50px;
          font-family: 'Inter', sans-serif;
          font-weight: 300;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          transform: translate(5px, -2px);
        }

        .output-label > h2 {
          font-size: 12px;
          white-space: nowrap;
          user-select: none;
        }
      `}</style>
    </div>
  )
}
